import { storeToRefs } from "pinia"

import { useAppStore } from "@/stores/AppStore"

import gtmAddToCart from "./gtmAddToCart.js"
import gtmCheckout from "./gtmCheckout.js"
import gtmPageView from "./gtmPageView.js"
import gtmPurchase from "./gtmPurchase.js"
import gtmRemoveFromCart from "./gtmRemoveFromCart.js"
import gtmViewItem from "./gtmViewItem.js"
import gtmViewItemList from "./gtmViewItemList.js"
import gtmLogin from "./gtmLogin.js"
import gtmLogout from "./gtmLogout.js"
import { getUserId, setUserIdFromInternalId, clearUserId } from "@/services/userIdentity"

type LoggedInCustomerInfo = {
    email: string
    phone: string
}

type PageViewParams = {
    type: string
    url: string
    title?: string
    categories?: [string]
}

type ViewItemListParams = {
    category: any
    products: any
}

type ProductParams = {
    product: any
    isFavorite: boolean
}

type ProductQuantityParams = ProductParams & {
    quantity: number
    source?: string
}

type CheckoutParams = {
    cart: any
    eventName: string
}

export class GtmEventEmitter {
    private static getUserId(): string | null {
        return getUserId();
    }

    static async syncTrackingUserId(): Promise<void> {
        if (getUserId()) return
        const appStore = useAppStore()
        const { getCurrentCustomer, isSignedIn } = storeToRefs(appStore)
        if (isSignedIn.value && getCurrentCustomer.value?.id != null) {
            await setUserIdFromInternalId(getCurrentCustomer.value.id)
        }
    }

    static async login(customer: { id: string }) {
        await setUserIdFromInternalId(customer.id)
        gtmLogin(getUserId())
    }

    static logout() {
        gtmLogout()
        clearUserId()
    }

    static async pageView(params: PageViewParams) {
        await this.syncTrackingUserId();
        gtmPageView(this.getLoggedInCustomerInfo(), this.getUserId(), params)
    }

    static viewItemList(params: ViewItemListParams) {
        gtmViewItemList(
            this.getLoggedInCustomerInfo(),
            this.getUserId(),
            params.category,
            params.products,
        )
    }

    static viewItem(params: ProductParams) {
        gtmViewItem(
            this.getLoggedInCustomerInfo(),
            this.getUserId(),
            params.product,
            { isFavorite: params.isFavorite },
        )
    }

    static addToCart(params: ProductQuantityParams) {
        gtmAddToCart(
            this.getLoggedInCustomerInfo(),
            this.getUserId(),
            params.product,
            {
                quantity: params.quantity,
                isFavorite: params.isFavorite,
                source: params.source,
            },
        )
    }

    static removeFromCart(params: ProductQuantityParams) {
        gtmRemoveFromCart(
            this.getLoggedInCustomerInfo(),
            this.getUserId(),
            params.product,
            {
                quantity: params.quantity,
                isFavorite: params.isFavorite,
                source: params.source,
            },
        )
    }

    static checkout(params: CheckoutParams) {
        gtmCheckout(
            this.getLoggedInCustomerInfo(),
            this.getUserId(),
            params.cart,
            params.eventName,
        )
    }

    static purchase(order: any) {
        gtmPurchase(this.getUserId(), order)
    }

    static getLoggedInCustomerInfo(): LoggedInCustomerInfo | null {
        const appStore = useAppStore()
        const { getCurrentCustomer, isSignedIn } = storeToRefs(appStore)

        if (!isSignedIn.value) return null

        const customer: any = getCurrentCustomer.value
        return {
            email: customer.email,
            phone: `${customer.phonePrefix}${customer.phone}`,
        }
    }
}
